import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ScreenOrientationType,
  Workspace,
  UserTokens,
  UserData,
  SendOTPResponse,
  CreateUserResponse,
  UserPermissionsResponse,
  ResendOTPResponse,
} from './userAuth.interface';
import { RootState } from '../root/root-types';
import { CatalogueView, Config } from '../config/config-model';

export const USER_AUTH_FEATURE_KEY = 'userAuth';

export type CatalogueConfigPayload = {
  catalogueView: CatalogueView;
  displayOutOfStock?: boolean;
};

export type CatalogueConfigSelection = {
  catalogueView: CatalogueView;
  showOutOfStockTag: boolean;
};

/*
 * Update these interfaces according to your requirements.
 */

export interface UserAuthState {
  token: string;
  userData: UserData;
  refreshToken: string;
  catalogPreference: undefined | CatalogueView;
  catologueView: CatalogueView;
  displayOutOfStock?: boolean;
  screenOrientation: ScreenOrientationType;
  workspace: Workspace | null;
  workspaces: Workspace[];
  isAuthenticated: boolean;
  otp: string;
  emailOtp: string;
  resendOtp: string;
  userPermissions: string[];
  loginTracked: boolean;
  userRoles: string[];
  drawerConfig: Config;
  advanceAmountConfig: Config;
}

export const initialUserAuthState: UserAuthState = {
  token: '',
  refreshToken: '',
  otp: '',
  emailOtp: '',
  resendOtp: '',
  userPermissions: [],
  userData: {
    mobile: '',
    firstName: '',
    email: '',
    lastName: '',
    id: '',
    role: '',
    userType: '',
    token: '',
    refreshToken: '',
    isVerified: false,
    integrationToken: '',
    userProfile: '',
    workspaceRoles: [],
    loggedInOnBehalfData: {
      supportUserToken: '',
      supportUserRefereshToken: '',
      isLoggedInOnBehalf: false,
      workspaceId: '',
    },
  },
  loginTracked: false,
  catalogPreference: undefined,
  catologueView: CatalogueView.ListView,
  displayOutOfStock: undefined,
  screenOrientation: ScreenOrientationType.PORTRAIT,
  workspace: null,
  isAuthenticated: false,
  userRoles: [],
  workspaces: [],
  drawerConfig: {},
  advanceAmountConfig: {},
};

export const userAuthSlice = createSlice({
  name: USER_AUTH_FEATURE_KEY,
  initialState: initialUserAuthState,
  reducers: {
    OnGuestUserAuthenticated(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    onRefreshToken(state, action: PayloadAction<UserTokens>) {
      state.refreshToken = action.payload.refreshToken;
      state.token = action.payload.token;
    },
    updateCatalogPreference(
      state,
      action: PayloadAction<CatalogueView | undefined>
    ) {
      state.catalogPreference = action.payload;
    },
    updateCatalogConfig(
      state,
      action: PayloadAction<CatalogueView | CatalogueConfigPayload>
    ) {
      const p = action.payload;
      if (typeof p === 'object' && p !== null && 'catalogueView' in p) {
        state.catologueView = p.catalogueView;
        state.displayOutOfStock = p.displayOutOfStock;
      } else {
        state.catologueView = p as CatalogueView;
      }
    },
    updateDrawerConfig(state, action: PayloadAction<Config>) {
      state.drawerConfig = action.payload;
    },
    updateAdvanceAmountConfig(state, action: PayloadAction<Config>) {
      state.advanceAmountConfig = action.payload;
    },
    updateScreenOrientation(
      state,
      action: PayloadAction<ScreenOrientationType>
    ) {
      state.screenOrientation = action.payload;
    },
    updateWorkspace(state, action: PayloadAction<Workspace>) {
      state.workspace = action.payload;
    },
    updateWorkspaces(state, action: PayloadAction<Workspace[]>) {
      state.workspaces = action.payload;
    },
    authenticate(state) {
      state.isAuthenticated = true;
    },

    onAuthSuccess(state, action: PayloadAction<UserData>) {
      state.userData = {
        ...action.payload,
        inviteUserType:
          action.payload.inviteUserType ||
          action.payload?.inviteDetails?.userType, // made a workaround for bug in returnsScreen, need to find rootcause
      };
      if (action.payload.temptoken) {
        state.token = action.payload.temptoken;
      } else {
        state.token = action.payload.token;
      }
      state.refreshToken = action.payload.refreshToken;
    },
    onSendOTPSuccess(state, action: PayloadAction<SendOTPResponse>) {
      state.otp = action?.payload?.mobile?.otp?.toString();
      state.emailOtp = action?.payload?.email?.otp?.toString();
    },
    onResendOTPSuccess(state, action: PayloadAction<ResendOTPResponse>) {
      state.otp = action?.payload?.otp?.toString();
      state.resendOtp = action?.payload?.otp?.toString();
    },
    updateOtp(state, action: PayloadAction<string>) {
      state.otp = action.payload;
    },
    updateUser(state, action: PayloadAction<CreateUserResponse | UserData>) {
      state.userData = { ...state.userData, ...action.payload };
    },
    updateUserPermissions(
      state,
      action: PayloadAction<UserPermissionsResponse[]>
    ) {
      state.userPermissions = action?.payload?.flatMap((item) =>
        item?.permissions?.map((permission) => permission.slug)
      );
    },
    updateUserRoles(state, action: PayloadAction<string[]>) {
      state.userRoles = action.payload;
      state.userData.workspaceRoles = action.payload;
    },
    updateLogInTracked(state, action: PayloadAction<boolean>) {
      state.loginTracked = action.payload;
    },
    logOut(state) {
      state.isAuthenticated = initialUserAuthState.isAuthenticated;
      state.otp = initialUserAuthState.otp;
      state.emailOtp = initialUserAuthState.emailOtp;
      state.token = initialUserAuthState.token;
      state.refreshToken = initialUserAuthState.refreshToken;
      state.userData = initialUserAuthState.userData;
      state.workspace = initialUserAuthState.workspace;
      state.workspaces = initialUserAuthState.workspaces;
      state.catologueView = initialUserAuthState.catologueView;
      state.displayOutOfStock = initialUserAuthState.displayOutOfStock;
    },
  },
});

export const userauthReducer = userAuthSlice.reducer;

export const {
  OnGuestUserAuthenticated,
  updateCatalogPreference,
  updateCatalogConfig,
  updateDrawerConfig,
  updateScreenOrientation,
  updateWorkspace,
  onAuthSuccess,
  authenticate,
  onRefreshToken,
  onSendOTPSuccess,
  updateUser,
  updateLogInTracked,
  updateOtp,
  updateUserRoles,
  updateUserPermissions,
  onResendOTPSuccess,
  logOut,
  updateWorkspaces,
  updateAdvanceAmountConfig,
} = userAuthSlice.actions;

export const getUserAuthState = (rootState: RootState): UserAuthState =>
  rootState[USER_AUTH_FEATURE_KEY];

export const getToken = createSelector(
  getUserAuthState,
  (state) => state.token
);

export const getRefreshToken = createSelector(
  getUserAuthState,
  (state) => state.refreshToken
);

export const getCatalogPreference = createSelector(
  getUserAuthState,
  (state) => state.catalogPreference
);

export const getCatalogConfig = createSelector(
  getUserAuthState,
  (state): CatalogueConfigSelection => ({
    catalogueView: state.catologueView,
    showOutOfStockTag: state.displayOutOfStock !== false,
  })
);

export const getDrawerConfig = createSelector(
  getUserAuthState,
  (state) => state.drawerConfig
);

export const getAdvanceAmountConfig = createSelector(
  getUserAuthState,
  (state) => state.advanceAmountConfig
);

export const getScreenOrientation = createSelector(
  getUserAuthState,
  (state) => state.screenOrientation
);

export const getWorkspace = createSelector(
  getUserAuthState,
  (state) => state.workspace
);

export const getWorkspaces = createSelector(
  getUserAuthState,
  (state) => state.workspaces
);

export const getWorkspaceId = createSelector(
  getUserAuthState,
  (state) => state?.workspace?.id || ''
);

export const getIsAuthenticated = createSelector(
  getUserAuthState,
  (state) => state.isAuthenticated
);

export const getUserData = createSelector(
  getUserAuthState,
  (state) => state.userData
);

export const getUserAuthId = createSelector(
  getUserAuthState,
  (state) => state.userData?.id
);

export const getOtp = createSelector(getUserAuthState, (state) => state.otp);

export const getEmailOtp = createSelector(
  getUserAuthState,
  (state) => state.emailOtp
);

export const getLogInTracked = createSelector(
  getUserAuthState,
  (state) => state.loginTracked
);

export const getUserAuthPermissions = createSelector(
  getUserAuthState,
  (state) => state.userPermissions
);
